import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/platform/platformUi";

export default function PlatformUsers() {
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [role, setRole] = useState("");
    const [active, setActive] = useState("");
    const [busy, setBusy] = useState(false);

    const load = () => {
        api.get("/platform/users", { params: { q, role, active } })
            .then((r) => setUsers(r.data || []))
            .catch(() => toast.error("Could not load users"));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, role, active]);

    const toggleActive = async (u) => {
        setBusy(true);
        try {
            await api.patch(`/platform/users/${u.id}`, { active: !u.active });
            toast.success(u.active ? "User deactivated" : "User activated");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        } finally {
            setBusy(false);
        }
    };

    const changeRole = async (u, newRole) => {
        if (newRole === u.role) return;
        setBusy(true);
        try {
            await api.patch(`/platform/users/${u.id}`, { role: newRole });
            toast.success("Role updated");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="platform-users-page">
            <PageHeader
                eyebrow="Users"
                title="All salon users"
                subtitle={`${users.length} users across all tenants — super admins excluded`}
            />

            <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                    <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Search</Label>
                    <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-48" placeholder="name / email / mobile" />
                </div>
                <div className="space-y-1">
                    <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Role</Label>
                    <select value={role} onChange={(e) => setRole(e.target.value)} className="h-9 rounded-md border border-[#E8E4DE] bg-white px-3 text-sm">
                        <option value="">All</option>
                        <option value="owner">Owner</option>
                        <option value="manager">Manager</option>
                        <option value="receptionist">Receptionist</option>
                        <option value="stylist">Stylist</option>
                        <option value="therapist">Therapist</option>
                    </select>
                </div>
                <div className="space-y-1">
                    <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Active</Label>
                    <select value={active} onChange={(e) => setActive(e.target.value)} className="h-9 rounded-md border border-[#E8E4DE] bg-white px-3 text-sm">
                        <option value="">All</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                    </select>
                </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-[#E8E4DE] bg-white">
                <table className="min-w-max w-full border-collapse">
                    <thead>
                        <tr className="border-b border-[#E8E4DE] text-left text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">
                            <th className="px-4 py-3">User</th>
                            <th className="px-4 py-3">Salon</th>
                            <th className="px-4 py-3">Role</th>
                            <th className="px-4 py-3">Mobile</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr key={u.id} className="border-b border-[#E8E4DE]/50 last:border-b-0">
                                <td className="px-4 py-3">
                                    <div className="font-semibold text-[#4A0E17]">{u.full_name}</div>
                                    <div className="text-xs text-[#8A8178]">{u.email}</div>
                                </td>
                                <td className="px-4 py-3">
                                    {u.tenant ? (
                                        <Link to={`/platform/salons/${u.tenant_id}`} className="text-sm font-medium text-[#3D2817] hover:text-[#C9A227]">
                                            {u.tenant.name}
                                        </Link>
                                    ) : "—"}
                                </td>
                                <td className="px-4 py-3 capitalize text-[#3D2817]">
                                    <select
                                        value={u.role}
                                        disabled={busy}
                                        onChange={(e) => changeRole(u, e.target.value)}
                                        className="h-8 rounded-md border border-[#E8E4DE] bg-white px-2 text-sm capitalize"
                                    >
                                        <option value="owner">Owner</option>
                                        <option value="manager">Manager</option>
                                        <option value="receptionist">Receptionist</option>
                                        <option value="stylist">Stylist</option>
                                        <option value="therapist">Therapist</option>
                                    </select>
                                </td>
                                <td className="px-4 py-3 text-sm text-[#3D2817]">{u.mobile || "—"}</td>
                                <td className="px-4 py-3">
                                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${u.active ? "bg-[#DCEEDC] text-[#1F7A55]" : "bg-[#FEF5F3] text-[#C84B31]"}`}>
                                        {u.active ? "Active" : "Inactive"}
                                    </span>
                                </td>
                                <td className="px-4 py-3">
                                    <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => toggleActive(u)}>
                                        {u.active ? "Deactivate" : "Activate"}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {users.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-4 py-10 text-center text-sm text-[#8A8178]">No users found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
